// The Chalo world as props (docs/plans/2026-10-17-asset-phases.md, Phase 1): the rings' barricades, the police lines
// in the gaps, booths and CCTV poles, the metro gates, the van and the bus, the stage, water and medic points. Placed
// once from rings.json and ChaloGeo; the snapshot only says where each police line stands and which metro gates are
// shut. drawCordon() stays under them as the tint, so the rings still read at minimap zoom.
// Red lines (plan section 10): police lines and barricades slide, never tip; nothing carries words.
import type { GameMap, Vec } from '../shared/map';
import { ChaloData, ChaloGeo, ChaloSnap, CODE_GATE, GateState } from '../shared/chalo';
import type { Standing } from './render';
import { standing, Tier, turnFor } from './props';
import SPRITES from './sprites.json';

/** A prop to stand: a sprites.json key with its foot at (x, y), world px. `z` overrides the depth (flat things). */
export interface Item { key: string; x: number; y: number; tier?: Tier; z?: number }

/** Metres between barricades along a ring (a steel barricade at body tier is about 7 m long). */
export const STEP_M = 7;
/** A barricade's half length along the ring: no part of one stands in a gap. */
export const HALF_M = 3.5;
/** Ring barricades stand on the ring's outer edge. */
export const OUT_M = 1.5;
/** Metres between police barriers across a gap. */
export const LINE_M = 5.4;
/** Seconds a police line takes to slide aside when the gate opens, and back into the gap. */
export const SLIDE_OPEN_S = 1.5, SLIDE_BACK_S = 3;

const BARRICADE = 'oga-concert-props/barricade';
const LINE = 'kenney-retro-urban-kit/detail-barrier-type-a', GATE = 'kenney-city-kit-roads/construction-barrier';
const sprites = SPRITES.sprites as Record<string, { w: number; h: number; ax: number; ay: number; ppm?: number }>;

const rad = (d: number) => (d * Math.PI) / 180;

interface Line {
  /** the checkpoint index; inner gates use construction barriers */
  k: number; key: string;
  /** barrier feet across the gap, and where each waits while the gate is open */
  gap: Vec[]; aside: Vec[];
  /** 0 = across the gap, 1 = aside; eased toward the state's target */
  p: number; last: GateState | null;
}

export class ChaloWorld {
  /** Hide the parked vehicles (Phase 4 drives them off). */
  hide = { van: false, bus: false };
  /** The banner at the stage: rolled (not shown) until the banner task raises it. */
  bannerUp = false;
  /** How many props stood in the last view (debug). */
  drawn = 0;

  readonly metre: number;
  /** ring barricades, per ring, in order round the ring (also read by the tests) */
  readonly cordon: (Item & { ring: number })[][] = [];
  readonly lines: Line[] = [];
  readonly booths: (Item & { cp: string })[] = [];
  readonly poles: (Item & { cp: string })[] = [];
  readonly metros: (Vec & { name: string; closesAtS: number | null })[] = [];
  private statics: Item[] = [];
  private van: Item; private bus: Item; private banner: Item;
  private stand = new WeakMap<Item, Standing | null>();

  constructor(map: GameMap, private geo: ChaloGeo, D: ChaloData) {
    const M = (this.metre = map.mpt ? map.tile / map.mpt : 15), T = map.tile, R = D.rings, c = geo.centre;
    const tileAt = (x: number, y: number) => {
      const tx = Math.floor(x / T), ty = Math.floor(y / T);
      return tx < 0 || ty < 0 || tx >= map.w || ty >= map.h ? -1 : ty * map.w + tx;
    };
    const inGap = (x: number, y: number) => { const i = tileAt(x, y); return i >= 0 && geo.gapOf[i] > 0; };
    const free = (x: number, y: number) => { const i = tileAt(x, y); return i >= 0 && !map.solid[i] && !geo.cordon[i] && !geo.gapOf[i]; };

    // Circle cordons are gone: the barricades stand on the streets (CordonProps, docs/plans/2026-10-18-chokepoints.md).
    // A ring that still carries a radius keeps the old placement.
    R.rings.forEach((ring, ri) => {
      const radiusM = (ring as { radiusM?: number }).radiusM;
      if (radiusM === undefined) { this.cordon.push([]); return; }
      const own = new Set(ring.cordon.map((t) => t[1] * map.w + t[0]));
      const r = radiusM * M, ro = (radiusM + OUT_M) * M, n = Math.round((2 * Math.PI * (radiusM + OUT_M)) / STEP_M);
      const row: (Item & { ring: number })[] = [];
      for (let i = 0; i < n; i++) {
        const a = (2 * Math.PI * i) / n, ca = Math.cos(a), sa = Math.sin(a);
        let x = c.x + ro * ca, y = c.y + ro * sa;
        if (!own.has(tileAt(c.x + r * ca, c.y + r * sa)) && !own.has(tileAt(x, y))) {
          // the circle crosses a building's corner here while the band beside it is open: stand on the nearest cordon
          // tile of the ring, as close to the circle as it allows
          const tx = Math.floor(x / T), ty = Math.floor(y / T);
          let best: Vec | null = null, bd = Infinity;
          for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
            if (!own.has((ty + dy) * map.w + tx + dx)) continue;
            const px = Math.max((tx + dx + 0.15) * T, Math.min((tx + dx + 0.85) * T, x)), py = Math.max((ty + dy + 0.15) * T, Math.min((ty + dy + 0.85) * T, y));
            const d = Math.hypot(px - x, py - y);
            if (d < bd) { bd = d; best = { x: px, y: py }; }
          }
          if (!best) continue;
          x = best.x; y = best.y;
        }
        const hx = -sa * HALF_M * M, hy = ca * HALF_M * M;
        if (inGap(x, y) || inGap(x + hx, y + hy) || inGap(x - hx, y - hy)) continue;
        const prev = row[row.length - 1];
        if (prev && Math.hypot(prev.x - x, prev.y - y) < STEP_M * 0.6 * M) continue; // two pulled onto the same tile
        row.push({ key: `${BARRICADE}@${turnFor((a * 180) / Math.PI + 90)}`, x, y, ring: ri });
      }
      this.cordon.push(row);
    });

    geo.cps.forEach((cp, k) => {
      const f = rad(R.checkpoints[k].facing), out = { x: Math.cos(f), y: Math.sin(f) }, tan = { x: -out.y, y: out.x };
      // the gap's width across, from its tiles projected on the tangent
      const along = cp.gaps.map((g) => ((g % map.w) + 0.5) * T * tan.x + (Math.floor(g / map.w) + 0.5) * T * tan.y - (cp.at.x * tan.x + cp.at.y * tan.y));
      const lo = Math.min(...along) - T / 2, hi = Math.max(...along) + T / 2, span = hi - lo, mid = (lo + hi) / 2;
      const n = Math.max(2, Math.ceil(span / (LINE_M * M)));
      // the construction barrier's model lies a quarter turn from the others' (its @0 is end-on), so it turns 90 more
      const inner = cp.ring === geo.ringNames.length - 1, turn = turnFor(R.checkpoints[k].facing + 90 + (inner ? 90 : 0));
      const gap: Vec[] = [], aside: Vec[] = [];
      // aside: the same line lying along the cordon at `aside`, a step in from it so it does not sit on the barricades
      const ax = cp.aside.x - out.x * 2.5 * M, ay = cp.aside.y - out.y * 2.5 * M;
      const da = Math.atan2(ay - c.y, ax - c.x), atan = { x: -Math.sin(da), y: Math.cos(da) };
      for (let j = 0; j < n; j++) {
        const s = mid + ((j + 0.5) / n - 0.5) * span, sa = ((j + 0.5) / n - 0.5) * n * LINE_M * M * 0.8;
        gap.push({ x: cp.at.x + tan.x * s, y: cp.at.y + tan.y * s });
        aside.push({ x: ax + atan.x * sa, y: ay + atan.y * sa });
      }
      this.lines.push({ k, key: `${inner ? GATE : LINE}@${turn}`, gap, aside, p: 0, last: null });

      // the CCTV pole at the gap's edge, a little inside; the booth (outer ring) about 12 m beside the gap, inside the
      // ring, on whichever side has room (away from where the line waits aside)
      const asideSide = Math.sign((cp.aside.x - cp.at.x) * tan.x + (cp.aside.y - cp.at.y) * tan.y) || 1;
      const sides = [-asideSide, asideSide];
      const at = (side: number, s: number, inM: number) => ({ x: cp.at.x + tan.x * side * s - out.x * inM * M, y: cp.at.y + tan.y * side * s - out.y * inM * M });
      const edge = sides.map((sd) => at(sd, (sd > 0 ? hi : -lo) + 0.5 * M, 2.5)).find((p) => !inGap(p.x, p.y)) ?? at(sides[0], span / 2 + M, 2.5);
      this.poles.push({ key: 'cctv_pole', x: edge.x, y: edge.y, cp: cp.id });
      if (cp.ring === 0) {
        // on the pavement inside the line where there is one (the walled compounds leave the road its kerbs only), else
        // any free ground
        const cands = sides.flatMap((sd) => [6, 3, 1, 10, 14, 18, 22, 26, 30, 34].flatMap((s) => [4, 7, 10].map((inM) => at(sd, (sd > 0 ? hi : -lo) + s * M, inM))));
        const apart = (p: Vec) => this.booths.every((o) => Math.hypot(o.x - p.x, o.y - p.y) > 2 * T); // two gates of a barricade: a booth each side
        const kerb = (p: Vec) => free(p.x, p.y) && apart(p) && map.rows[Math.floor(p.y / T)][Math.floor(p.x / T)] === '.';
        const b = cands.find(kerb) ?? cands.find((p) => free(p.x, p.y)) ?? cands[0];
        this.booths.push({ key: 'police_booth', x: b.x, y: b.y, cp: cp.id });
      }
    });

    // metro gates: the mouth to the street; open until the director shuts them
    for (const e of R.entries) if (e.kind === 'metro') this.metros.push({ x: e.x * T, y: e.y * T, name: e.name, closesAtS: e.closesAtS });

    // the stage at the centre: the dais, speakers at its front corners, the mic in front; a medic box at a corner;
    // water (a barrel and two buckets) beside the stage and each metro gate
    const S = this.statics, at = (dx: number, dy: number, key: string): Item => ({ key, x: c.x + dx * M, y: c.y + dy * M });
    S.push(at(0, 0, 'oga-concert-props/stage-005~805A3A'));
    S.push(at(-8.5, 3.5, 'oga-concert-props/speaker-005'), at(8.5, 3.5, 'oga-concert-props/speaker-005'));
    S.push(at(-6, 4.5, 'oga-concert-props/speaker-004'), at(0, 5.5, 'oga-concert-props/mic-001'));
    S.push(at(-10.5, 7, 'first_aid_box'));
    const water = (x: number, y: number) => S.push({ key: 'kenney-survival-kit/barrel', x, y }, { key: 'kenney-survival-kit/bucket', x: x + 1.6 * M, y: y + 0.6 * M }, { key: 'kenney-survival-kit/bucket', x: x + 0.4 * M, y: y + 1.5 * M });
    water(c.x + 10.5 * M, c.y + 6.5 * M);
    for (const m of this.metros) {
      S.push({ key: 'metro_gate_open', x: m.x, y: m.y }); // replaced by the shut gate in items()
      water(m.x + 7 * M, m.y + 3 * M);
    }
    this.banner = at(-7, -2, 'kenney-fantasy-town-kit/banner-red~F1ECE0');
    this.van = { key: 'surveillance_van', x: geo.van.x, y: geo.van.y };
    this.bus = { key: 'quaternius-public-transport/Bus@0', x: geo.bus.x, y: geo.bus.y };
    for (const row of this.cordon) S.push(...row);
    for (const p of this.booths) S.push(p);
    for (const p of this.poles) S.push(p);
  }

  /** Move each police line toward where its gate's state puts it. The first snapshot puts them there at once. */
  update(ch: ChaloSnap | null, dt: number) {
    for (const L of this.lines) {
      const st = ch ? CODE_GATE[ch.cs[L.k]?.[0] ?? 0] : 'closed';
      const target = st === 'open' ? 1 : 0;
      if (L.last === null) L.p = target;
      else if (st === 'open') L.p = Math.min(1, L.p + dt / SLIDE_OPEN_S);
      else L.p = Math.max(0, L.p - dt / SLIDE_BACK_S); // back (and closed, if police got home first): slide into the gap
      L.last = st;
    }
  }

  /** Where checkpoint k's police barriers stand now. */
  lineAt(k: number): Vec[] {
    const L = this.lines[k], e = L.p * L.p * (3 - 2 * L.p);
    return L.gap.map((g, j) => ({ x: g.x + (L.aside[j].x - g.x) * e, y: g.y + (L.aside[j].y - g.y) * e }));
  }

  /** Every prop for this snapshot, unculled. */
  items(ch: ChaloSnap | null): Item[] {
    const out: Item[] = [];
    const t = ch?.t ?? 0;
    for (const it of this.statics) {
      if (it.key === 'metro_gate_open') {
        const m = this.metros.find((q) => q.x === it.x && q.y === it.y);
        if (m && m.closesAtS !== null && t >= m.closesAtS) { out.push(this.shut(it)); continue; }
      }
      out.push(it);
    }
    // The street barricades, including the line across each gap, are drawn by CordonProps. lineAt() still answers tests.
    if (!this.hide.van) out.push(this.van);
    if (!this.hide.bus) out.push(this.bus);
    if (this.bannerUp) out.push(this.banner);
    // games' furniture, once the games exist: a sit-in's durries in the gap, a talk's table at the inspector
    if (ch) this.geo.cps.forEach((cp, k) => {
      if (CODE_GATE[ch.cs[k]?.[0] ?? 0] !== 'game') return;
      for (const it of this.gameProps(k)) out.push(it);
    });
    return out;
  }

  private shuts = new Map<Item, Item>();
  private shut(it: Item): Item {
    let s = this.shuts.get(it);
    if (!s) { s = { ...it, key: 'metro_gate_shut' }; this.shuts.set(it, s); }
    return s;
  }

  private games = new Map<number, Item[]>();
  /** The furniture checkpoint k's game stands while it runs (by its mission). */
  gameProps(k: number): Item[] {
    const hit = this.games.get(k);
    if (hit) return hit;
    const cp = this.geo.cps[k], M = this.metre, out: Item[] = [];
    const d = Math.atan2(cp.at.y - this.geo.centre.y, cp.at.x - this.geo.centre.x), o = { x: Math.cos(d), y: Math.sin(d) };
    const p = (s: number, a: number) => ({ x: cp.at.x + o.x * s * M - o.y * a * M, y: cp.at.y + o.y * s * M + o.x * a * M });
    if (cp.mission === 'dharna') {
      // durries flat on the ground just outside the gap: under anyone sitting on them. The rug sprites' feet are at a
      // corner, so each is centred by its picture
      const a = p(5, -3.5), b = p(5, 3.5);
      out.push(this.centred('kenney-furniture-kit/rugRectangle~2F5D8C', a.x, a.y), this.centred('kenney-furniture-kit/rugRounded~8C3B2F', b.x, b.y));
    } else if (cp.mission === 'baat') {
      // the table at the inspector, just outside the gap, a chair either side
      const tb = p(4, 0), c1 = p(4, -2.2), c2 = p(4, 2.2);
      out.push({ key: 'kenney-furniture-kit/table', ...tb }, { key: 'kenney-furniture-kit/chair', ...c1 }, { key: 'kenney-furniture-kit/chair', ...c2 });
    }
    this.games.set(k, out);
    return out;
  }

  /** A flat prop centred on (x, y) by its picture, sorted under everything standing near it. */
  private centred(key: string, x: number, y: number): Item {
    const s = sprites[key];
    if (!s) return { key, x, y };
    const f = (3.2 * this.metre) / (s.ppm || SPRITES.ppm);
    const fx = x + (s.ax - s.w / 2) * f, fy = y + (s.ay - s.h / 2) * f;
    return { key, x: fx, y: fy, z: y - 6 * this.metre };
  }

  /** The props in the view, depth-sortable with the bodies and buildings (TreeLayer.draw's `extra`). */
  frame(ch: ChaloSnap | null, dt: number, vx0: number, vy0: number, vw: number, vh: number): Standing[] {
    this.update(ch, dt);
    // margins: the widest prop (the bus, about 19 m at the cars' scale) reaches this far from its foot
    const m = 24 * this.metre, x0 = vx0 - m, x1 = vx0 + vw + m, y0 = vy0 - m, y1 = vy0 + vh + m * 2;
    const out: Standing[] = [];
    for (const it of this.items(ch)) {
      if (it.x < x0 || it.x > x1 || it.y < y0 || it.y > y1) continue;
      let s = this.stand.get(it);
      if (s === undefined) {
        s = standing(it.key, it.x, it.y, this.metre, it.tier);
        if (s && it.z !== undefined) s.y = it.z;
        this.stand.set(it, s);
      }
      if (s) out.push(s);
    }
    this.drawn = out.length;
    return out;
  }
}
